/**
 * usePrivileges — Reads the logged-in user's role privileges from sessionStorage
 * and exposes helper methods for slug-based permission checks.
 *
 * Privilege slug pattern: "<module>.<action>"
 * Known actions: view | create | update | delete | export | import
 *
 * Examples:
 *   "course-management.view"        "course-management.create"
 *   "course-management.update"      "course-management.delete"
 *   "learner-management.view"       "learner-management.create"
 *   "category-management.view"      "module-management.view"
 *   "learning-path-management.view" "system-settings-management.view"
 *   "role-management.view"          "role-management.create"
 *   "role-management.update"        "role-management.delete"
 *
 * Usage:
 *   const { can, canAny, canAll, isSuperAdmin } = usePrivileges();
 *
 *   can('course-management.create')               → true/false
 *   canAny(['course-management.create', 'course-management.update'])
 *   canAll(['course-management.view', 'course-management.create'])
 */
const usePrivileges = () => {
  const user = JSON.parse(sessionStorage.getItem('user'));

  // Handle both `user.role` (object) and `user.roles[0]` (array) structures
  const activeRole = user?.role || user?.roles?.[0] || {};
  const roleSlug = activeRole?.slug ?? '';

  // Handle both `rolePrivilege` and `rolePrivileges` keys
  const privileges = activeRole?.rolePrivilege || activeRole?.rolePrivileges || [];

  // Build a Set of all privilege slugs for O(1) lookup
  const privilegeSet = new Set(
    privileges
      .map((rp) => rp?.moduleSubAppPrivilege?.slug)
      .filter(Boolean)
  );

  // Only true system super-admins bypass slug checks.
  // client-admin, viewer, and all other roles must have the explicit privilege.
  const isSuperAdmin = roleSlug === 'lms-admin' || roleSlug === 'super-admin';

  // DEV DEBUG — remove before production
  if (process.env.NODE_ENV === 'development') {
    console.log('[usePrivileges] roleSlug:', roleSlug);
    console.log('[usePrivileges] isSuperAdmin:', isSuperAdmin);
    console.log('[usePrivileges] privilegeSet slugs:', [...privilegeSet]);
  }

  /**
   * can(slug) — returns true if user has the given privilege.
   * - null/undefined slug → always visible (no restriction).
   * - super-admin roles bypass all checks.
   */
  const can = (slug) => {
    if (!slug) return true;
    if (isSuperAdmin) return true;
    return privilegeSet.has(slug);
  };

  /**
   * canAny(slugs) — returns true if user has AT LEAST ONE of the slugs.
   * Useful for showing a section when the user can either create OR update.
   */
  const canAny = (slugs = []) => {
    if (!slugs.length) return true;
    if (isSuperAdmin) return true;
    return slugs.some((slug) => privilegeSet.has(slug));
  };

  /**
   * canAll(slugs) — returns true only if user has ALL of the slugs.
   */
  const canAll = (slugs = []) => {
    if (!slugs.length) return true;
    if (isSuperAdmin) return true;
    return slugs.every((slug) => privilegeSet.has(slug));
  };

  return {
    can,
    canAny,
    canAll,
    user,
    roleSlug,
    privilegeSet,
    isClientAdmin: roleSlug === 'client-admin',
    isSuperAdmin,
  };
};

export default usePrivileges;
